import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { setApiTarget } from "../api/request.js";
import { screenRoleOf, type AdminRole } from "../api/types.js";
import { NILE_TABS, routeHref, type NileTab, type Router } from "../router.js";
import { useAdminSession } from "../session/use-admin-session.js";
import { useAdminWallet } from "../session/wallet-context.js";
import { SignInScreen } from "../sign-in.js";
import { Button, ScreenHeader, Spinner } from "../ui.js";
import { MonitoringScreen } from "./monitoring.js";
import { OrderDetailScreen, OrdersScreen } from "./orders.js";
import { OwnSupplyHub } from "./own-supply.js";
import { ProvidersScreen } from "./providers.js";
import { WalletsScreen } from "./wallets.js";

/**
 * Nile, one menu row (owner 2026-10-20): the mainnet screens of the same names, pointed at the Nile API.
 *
 * ITS OWN SESSION (docs/product/admin-auth.md §9a): the Nile API has its own admin table, sessions and root. Under a
 * mainnet session the panel opens the Nile one from it, with its own browser key, no second sign-in (owner 2026-11-03);
 * the role shown and used here is Nile's answer. The Nile sign-in is shown only when that is refused.
 */

const TAB_LABELS: Record<NileTab, string> = {
  orders: "Orders",
  wallets: "Wallets",
  providers: "Providers",
  monitoring: "Monitoring",
  "own-supply": "Own supply",
};

/** Renders `children` with the Nile screen right once a Nile admin session is open; the Nile sign-in until then. */
export function NileGate({ children }: { children: (role: AdminRole) => ComponentChildren }) {
  const session = useAdminSession(useAdminWallet(), "nile");
  if (!session.ready) return <Spinner label="Checking the Nile session…" />;
  if (session.identity === null) return <SignInScreen session={session} network="Nile" embedded />;
  return (
    <>
      <div class="mb-2 flex items-center justify-end gap-2 text-[11px] text-ink-muted">
        <span class="num">Nile · {session.identity.role}</span>
        <Button tone="ghost" onClick={() => void session.signOut()}>
          Sign out of Nile
        </Button>
      </div>
      {children(screenRoleOf(session.identity.role))}
    </>
  );
}

/**
 * Every request of the subtree goes to the Nile API while it is mounted. Set during render, before any child's
 * effect starts a request; reset on unmount, before the next screen's effects run.
 */
function NileTarget({ children }: { children: ComponentChildren }) {
  setApiTarget("nile");
  useEffect(() => () => setApiTarget("mainnet"), []);
  return <>{children}</>;
}

export function NileScreen({ router }: { router: Router }) {
  const param = router.route.param;
  const tab: NileTab = NILE_TABS.find((t) => t === param) ?? "orders";
  // A Nile order is `/nile/orders/<id>`: the Orders screen's links and its back button stay inside this tab, and a
  // middle-click opens the Nile order, not the mainnet one.
  const orderId = tab === "orders" ? router.route.detail : null;
  const ordersRouter: Router = {
    route: { screen: "orders", param: orderId, detail: null },
    navigate: (screen, id) => router.navigate("nile", "orders", screen === "orders" ? id : undefined),
    onNavigate: (event, screen, id) => router.onNavigate(event, "nile", "orders", screen === "orders" ? id : undefined),
    hrefFor: (_screen, id) => routeHref("nile", "orders", id),
  };
  return (
    <>
      <ScreenHeader title="Nile" lead="The test network: its orders, wallets, providers, monitoring and own supply, read from the Nile API." />
      <nav class="mb-3 flex flex-wrap gap-1 border-b border-line" aria-label="Nile tabs">
        {NILE_TABS.map((t) => (
          <button
            key={t}
            type="button"
            aria-current={t === tab ? "page" : undefined}
            onClick={() => router.navigate("nile", t)}
            class={`-mb-px border-b-2 px-3 py-2 text-[12.5px] font-semibold ${t === tab ? "border-accent text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </nav>
      <NileGate>
        {(role) => (
          <NileTarget>
            {tab === "orders" ? (
              orderId === null ? (
                <OrdersScreen router={ordersRouter} />
              ) : (
                <OrderDetailScreen id={orderId} router={ordersRouter} />
              )
            ) : tab === "wallets" ? (
              <WalletsScreen />
            ) : tab === "providers" ? (
              <ProvidersScreen role={role} />
            ) : tab === "own-supply" ? (
              <OwnSupplyHub role={role} />
            ) : (
              <MonitoringScreen />
            )}
          </NileTarget>
        )}
      </NileGate>
    </>
  );
}
